import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { useSelector } from 'react-redux';
import queryString from 'query-string';
import { setTag, getCurrentScope, setContext, captureMessage } from '@sentry/gatsby';
import { Container, Row, Column, Button, Alert, Icon } from '@pella/sash-framework';
import { Breadcrumbs, Layout, SEO, LoadingOverlay, CTA } from '../../components';
import { monetateDataLayer } from '../../components/utilities/functions';
import Filter from './filters';
import PLPResults from './results';
import PLPFilterSort from './sort';
import PLPFilterTags from './tags';
import FAQ from '../../components/common/faq';
import Question from '../../components/common/structured-data/question';
import CardSection from '../../components/common/card-section';
import { PayPalMessages, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { useOktaAuth } from '../../hooks/use-okta-auth';
import { useCartHeader } from '../../hooks/use-cart-header';
import { useIsClient } from '@uidotdev/usehooks';
import { isValidArray } from '../../components/utilities/validations';
import { useProduct } from '../../hooks/use-product';
import './index.css';
import VisualNav from './visual-nav';

const PLP = (props) => {
	const pageData = props.data.contentstackStaticPlp;
	const pageConfig = props.data.contentstackPlpConfig;
	const initProductRequestBody = pageData.request_body ? JSON.parse(pageData.request_body) : null;
	const filtersArray = [];
	const { isLogged, profile } = useOktaAuth();
	const { cart } = useCartHeader();
	const { isClient } = useIsClient();
	const [pageLoading, setPageLoading] = useState(true);
	const [loading, setLoading] = useState(true);
	const [productQueryParams, setProductQueryParams] = useState(null);
	const [loadProductListing, setLoadProductListing] = useState(false);
	const [tileSort, setTileSort] = useState('recommended');
	const [productError, setProductError] = useState(null);
	const [clientID, setClientID] = useState('H03');
	const staticTiles = pageData.url === '/shop/windows/parts/screens/' ? props.data.allProductTiles?.nodes.filter((item) => item.type === 'screen') : pageData.url === '/shop/windows/parts/sashes/' ? props.data.allProductTiles?.nodes.filter((item) => item.type === 'sash') : [];
	const [{}, dispatch] = usePayPalScriptReducer();
	const { fetchProductList } = useProduct();

	const [tiles, setTiles] = useState(pageData.tiles?.length > 0 ? pageData.tiles : staticTiles);
	useEffect(() => {
		// moved browser api to useEffect to prevent hydration errors
		setTileSort(new URLSearchParams(window.location.search).get('sort') || 'recommended');
		setProductQueryParams(new URLSearchParams(window.location.search).get('options'));

		if (productQueryParams) {
			setTiles(null);
		} else if (pageData.manual && pageData.tiles) {
			setTiles(pageData.tiles);
		} else {
			//overwrites contentstack tile data with api data - ECV-9414
			setTiles(props.data.allProductTiles.nodes.filter((item) => pageData.tiles.find((tile) => tile.backend_name === item.backend_name)));
		}

		const chatLink = document.querySelector('.js-chatLink');
		if (chatLink) {
			document.addEventListener('click', onChatLinkClick);
			return () => document.removeEventListener('click', onChatLinkClick);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, []);

	useEffect(() => {
		dispatch({
			type: 'setLoadingStatus',
			value: 'pending',
		});
	}, [dispatch]);

	const locations = useSelector((state) => state.locations);
	const [isEcommerce, setIsEcommerce] = useState(null);
	const [isEastCoast, setIsEastCoast] = useState(true);
	const [isPartsOnly, setIsPartsOnly] = useState(false);
	useEffect(() => {
		if (!loading) {
			setPageLoading(true);
		}

		if (typeof locations.ecommerce === 'boolean' && locations.ecommerce !== isEcommerce) {
			setIsEcommerce(locations.ecommerce);
		}

		if (locations?.service_info?.branch_region === 'west') {
			setIsEastCoast(false);
		} else {
			setIsEastCoast(true);
		}

		if (typeof locations.parts_only === 'boolean' && isPartsOnly !== locations.parts_only) {
			setIsPartsOnly(locations.parts_only);
		}

		if (!loading) {
			setPageLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [locations.ecommerce, locations.parts_only, JSON.stringify(locations.service_info)]);

	const [isPro, setIsPro] = useState(null);

	useEffect(() => {
		if (profile.isPro && !isPro) {
			setIsPro(true);
			setClientID('H04');
			setLoadProductListing(true);
		} else if (!profile.isPro && (isPro || isPro === null)) {
			setIsPro(false);
			setClientID('H03');
			setLoadProductListing(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [profile.isPro, isPro]);

	const [dynamicPageState, setDynamicPageState] = useState(false);
	const [filters, setFilters] = useState(productQueryParams ? null : JSON.parse(pageData.filters));

	useEffect(() => {
		if (!productQueryParams && pageData.filters && pageData.dynamic_filters) {
			setFilters(JSON.parse(pageData.filters).concat(JSON.parse(pageData.dynamic_filters)));
		}
	}, [productQueryParams, pageData.dynamic_filters, pageData.filters]);

	function loadFromQueryParams(clientID) {
		setPageLoading(true);
		setDynamicPageState(true);

		new URLSearchParams(queryString.parse(window.location.search))
			.get('options')
			.split('|')
			.forEach((qaPair) => {
				const qaPairArray = qaPair.split('=');
				if (qaPairArray.length !== 2) return null;
				if (qaPairArray[1].indexOf(',') > -1) {
					qaPairArray[1].split(',').forEach(function (answer) {
						filtersArray.push({
							question_backend_name: qaPairArray[0],
							answer_backend_name: answer,
						});
					});
				} else {
					// Fix for unknown source of urls having query paramater with incorrect capitalization
					let altQuestion, altAnswer;
					if (qaPairArray[0] === 'products') {
						altQuestion = qaPairArray[0].replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase()); //capitalize first character of every word
						altAnswer = qaPairArray[1].replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase()); //capitalize first character of every word
					}

					filtersArray.push({
						question_backend_name: altQuestion ? altQuestion : qaPairArray[0],
						answer_backend_name: altAnswer ? altAnswer : qaPairArray[1],
					});
				}
			});

		getProductList(filtersArray, clientID);
	}

	function staticToDynamicConversion(filters, clientID) {
		setPageLoading(true);
		setDynamicPageState(true);
		const updatedQueryParams = [];

		const urlParams = new URLSearchParams(window.location.search);
		const pageParam = urlParams.get('page');

		if (initProductRequestBody) {
			filtersArray.push(...initProductRequestBody.filters);
			updatedQueryParams.push(...initProductRequestBody.filters.map((filter) => [filter.question_backend_name + '=' + filter.answer_backend_name.replace('+', '%2B')]));
		}

		const newUrl = window.location.pathname + '?options=' + updatedQueryParams.join('|').replace(',|', '|').replace('+', '%2B') + (pageParam ? `&page=${pageParam}` : '');
		history.replaceState(null, '', newUrl);

		getProductList(filtersArray, clientID);
	}

	const getProductList = async (filtersArray, clientID) => {
		setProductError(null);
		const productList = await fetchProductList(filtersArray.length > 0 ? { filters: filtersArray } : {}, clientID);

		if (productList?.data?.success) {
			// Filter tiles before setting to have more accurate numbers versus hiding/showing tile on tiles/index.js
			let updatedTiles = productList.data.tiles;
			if (!isEastCoast) {
				updatedTiles = productList.data.tiles.filter((tile) => tile.description !== 'Overstock');
			}

			// Added custom check to remove filter on parts plp
			setFilters(productList.data.product_list_questions.filter((question) => question.backend_name !== 'custom'));
			setTiles(updatedTiles); //overwrites contentstack tile data with api data - ECV-9414
			setLoadProductListing(false);
			setPageLoading(false);
			setLoading(false);
			monetateDataLayer('addProducts', productList.data.tiles?.[0] ? productList.data.tiles.map((item) => item.backend_name) : []);
		} else {
			setProductError(productList?.data?.error?.message || 'Unknown Error');
			setLoadProductListing(false);
			setTiles(null);
			setPageLoading(false);
			setLoading(false);
			setTag('Pella Component', 'PLP', 'API Transaction ID', productList?.data?.REQUEST_ID);
			getCurrentScope().setTransactionName('fetchProductList');
			setContext('Error', {
				errorData: JSON.stringify(productList?.data) || 'Unknown Error Data',
				responseURL: productList?.request?.responseURL,
				configData: productList?.config?.data,
			});
			captureMessage(productList?.data?.error?.message || 'Unknown Error Details');
		}
	};

	useEffect(() => {
		// If east/west changes and no tiles are available
		setLoadProductListing(true);
	}, [isEastCoast]);

	useEffect(() => {
		if (loadProductListing && typeof isEcommerce === 'boolean' && typeof isPro === 'boolean' && typeof isEastCoast === 'boolean') {
			if (!pageData.manual) {
				const hasQueryParams = new URLSearchParams(queryString.parse(window.location.search)).get('options');
				if (hasQueryParams) {
					loadFromQueryParams(clientID);
				} else {
					staticToDynamicConversion(filters, clientID);
				}
			} else {
				setPageLoading(false);
				setLoading(false);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [loadProductListing, isEcommerce, isPro, isEastCoast]);

	function clearFilters() {
		setPageLoading(true);
		history.replaceState(null, '', window.location.pathname);
		setFilters(
			JSON.parse(pageData.filters)
				.concat(JSON.parse(pageData.dynamic_filters))
				.filter((filter) => filter != null)
		);

		const updatedQueryParams = [];
		if (initProductRequestBody) {
			filtersArray.push(...initProductRequestBody.filters);
			updatedQueryParams.push(...initProductRequestBody.filters.map((filter) => [filter.question_backend_name + '=' + filter.answer_backend_name.replace('+', '%2B')]));
		}
		history.replaceState(null, '', window.location.pathname + '?options=' + updatedQueryParams.join('|').replace(',|', '|').replace('+', '%2B'));

		// setLoadProductListing(true);
		getProductList(filtersArray, clientID);
	}

	// Show/hide mobile filters
	const [showFilters, setShowFilters] = useState(false);
	const onMobileFilterClick = () => setShowFilters(true);
	const closeMobileFilters = () => setShowFilters(false);

	const [faqData, setFaqData] = useState([]);
	const fetchFAQData = (faqs) => {
		if (isValidArray(faqs)) {
			setFaqData([...faqData, ...faqs]);
		}
	};

	const [activeFilters, setActiveFilters] = useState(false);
	useEffect(() => {
		if (isValidArray(filters)) {
			setActiveFilters(false);
			filters.forEach((filter) => {
				if (
					filter.answers.some((answer) => answer.selected && !JSON.parse(pageData.filters).find((staticFilter) => staticFilter.backend_name === filter.backend_name)) || // non static questions
					filter.answers.some((answer) => answer.selected && JSON.parse(pageData.filters).find((staticFilter) => staticFilter.backend_name === filter.backend_name && !staticFilter.answers.some((answer) => answer.selected))) // static questions with non default answers
				) {
					setActiveFilters(true);
				}
			});
		}
	}, [filters, pageData.filters]);

	const [cartPrice, setCartPrice] = useState(0);
	useEffect(() => {
		if (cart?.pricing?.display_price?.subtotal?.amount > 0 && cart?.pricing?.display_price?.subtotal?.amount !== cartPrice) {
			setCartPrice(cart?.pricing?.display_price?.subtotal?.amount);
		}
	}, [cart?.pricing?.display_price?.subtotal?.amount, cartPrice]);

	const onChatLinkClick = (e) => {
		if (e.target.classList.contains('js-chatLink')) {
			e.preventDefault();
			const quiqMobileBtn = document.querySelector('.quiq-injected-child > button');
			const quiqDesktopBtn = document.querySelector('.quiq-action-toggle');
			if (quiqMobileBtn) quiqMobileBtn.click();
			if (quiqDesktopBtn) quiqDesktopBtn.click();
		}
	};

	const otherTiles = pageData?.evergreen_content?.find((item) => item.dynamic !== null)?.dynamic;

	return (
		<Layout
			id='template-PLP'
			pageType={pageData.seo.page_type}
		>
			{cartPrice / 100 < 10000 && (
				<div
					className='bg-gray-5 py-3'
					id='paypalHeader'
				>
					<Container>
						<PayPalMessages
							forceReRender={[cartPrice]}
							amount={cartPrice / 100}
							style={{
								logo: {
									type: 'inline',
								},
								text: {
									size: 16,
									align: 'center',
								},
							}}
							onClick={() => {
								window.dataLayer = window.dataLayer || [];
								window.dataLayer.push({
									event: 'paypal-click',
									paypal_page: 'plp',
									paypal_cart_total: cartPrice / 100,
								});
							}}
						/>
					</Container>
				</div>
			)}

			<Breadcrumbs
				data={pageData?.breadcrumb}
				currentUrl={pageData?.url}
				className='!pt-[18px] xl:!pb-1.5 xl:!pt-5'
			/>

			<Container className='pt-[18px]'>
				{pageData.out_of_sales?.top_bar && !isEcommerce && (
					<Alert
						type='error'
						hideClose={true}
						className={`mb-4 text-center`}
					>
						<div dangerouslySetInnerHTML={{ __html: pageData.out_of_sales.top_bar }} />
					</Alert>
				)}

				{!isEcommerce && isClient && (
					<Alert
						className='mb-4 text-center'
						id='alertFocus'
						aria-live='assertive'
						type='error'
						hideClose
					>
						{
							<div
								dangerouslySetInnerHTML={{
									__html: pageConfig.errors?.[pageConfig.errors?.findIndex((error) => error.field_id === 'international')]?.error,
								}}
							/>
						}
					</Alert>
				)}

				{showFilters && (
					<Button
						className='fixed left-0 top-0 z-6 h-screen !w-screen cursor-pointer !bg-black/75 hover:bg-black/75'
						onClick={() => closeMobileFilters()}
					/>
				)}

				<div className='plpGrid relative grid gap-x-4 xl:grid-cols-[250px_1fr] xl:items-start xl:gap-x-12'>
					<div className='plpGrid_Filters duration-200 xl:sticky xl:top-[110px] xl:col-span-1 xl:w-[250px] xl:pr-1'>
						<Filter
							staticFilters={JSON.parse(pageData.filters) ? JSON.parse(pageData.filters) : []}
							filters={filters ?? JSON.parse(pageData.filters)}
							pageUrl={pageData.url}
							productQueryParams={productQueryParams}
							reloadProductListing={[loadProductListing, setLoadProductListing]}
							dynamicPageState={dynamicPageState}
							showOnMobile={showFilters}
							isEastCoast={isEastCoast}
						/>
					</div>

					<div className='plpGrid-results xl:col-start-2'>
						<Row className='mb-6 xl:mb-8'>
							<Column
								xs={12}
								xl={8}
							>
								<h1 className='text-h2 mb-2.5'>{pageData?.top_content?.heading}</h1>
								<div
									dangerouslySetInnerHTML={{ __html: pageData?.top_content?.description }}
									className='tracking-normal text-black xl:text-body-2'
								/>
							</Column>

							<Column
								xl={4}
								className='self-end max-xl:hidden'
							>
								{tiles?.length >= 5 && <PLPFilterSort tileSort={[tileSort, setTileSort]} />}
							</Column>
						</Row>

						{isValidArray(pageData?.offramps?.card) && (
							<VisualNav
								placement={'top'}
								pageData={pageData}
							/>
						)}

						{pageData.url === '/shop/windows/parts/screens/' && pageConfig?.screen_finder && (
							<div className='mx-auto mb-7 max-w-[850px] border border-gray-15 p-5 text-center shadow-[0_2px_5px_rgba(0,0,0,0.25)] lg:p-[30px]'>
								{pageConfig.screen_finder.heading && <p className='mb-7 text-26px'>{pageConfig.screen_finder.heading}</p>}
								{pageConfig.screen_finder.cta?.[0] && <CTA data={pageConfig.screen_finder.cta[0]} />}
							</div>
						)}

						{pageData?.evergreen_content[0]?.finder_help?.cta_ribbon[0]?.ribbon_style === 'finder' && (
							<div className='mx-auto mb-7 max-w-[850px] border border-gray-15 p-5 text-center shadow-[0_2px_5px_rgba(0,0,0,0.25)] lg:p-[30px]'>
								{pageData?.evergreen_content[0]?.finder_help?.cta_ribbon[0]?.title && <p className='mb-7 text-26px'>{pageData.evergreen_content[0].finder_help.cta_ribbon[0].title}</p>}
								{pageData?.evergreen_content[0]?.finder_help?.cta_ribbon[0]?.group.ctas?.[0] && <CTA data={pageData.evergreen_content[0].finder_help.cta_ribbon[0].group.ctas[0]} />}
							</div>
						)}

						<div className='max-xl:mb-6'>
							<div className='flex flex-col items-end justify-between sm:flex-row'>
								<div className='row-span-1 mb-4 w-full sm:col-span-1 sm:mb-0 sm:mr-2.5 sm:max-w-[280px] sm:content-end xl:hidden'>
									<Button
										color='secondary'
										className='flex min-h-[54px] !w-full items-center justify-center'
										onClick={() => onMobileFilterClick()}
									>
										<Icon name='tune' />
										<p>Filter</p>
									</Button>
								</div>

								<div className='w-full justify-self-end sm:ml-2.5 sm:max-w-[280px] xl:hidden'>{tiles?.length >= 5 && <PLPFilterSort tileSort={[tileSort, setTileSort]} />}</div>
							</div>

							{!pageData.manual && (
								<>
									<PLPFilterTags
										staticFilters={JSON.parse(pageData.filters)}
										filters={filters}
										pageLoading={pageLoading}
										dynamicPageState={dynamicPageState}
										reloadCatalog={[loadProductListing, setLoadProductListing]}
										plpURL={pageData.url}
									/>

									{activeFilters && (
										<Button
											color='tertiary-alt'
											onClick={() => clearFilters()}
										>
											Clear Filters
										</Button>
									)}
								</>
							)}
						</div>

						{!pageData.manual && productError && (
							<Alert
								type='error'
								className='mb-4'
								hideClose
							>
								{productError}
							</Alert>
						)}

						<div
							className='relative'
							id='js-plpResults'
						>
							{pageLoading && <LoadingOverlay fixed={false} />}

							<PLPResults
								staticTiles={pageData.tiles}
								tiles={tiles}
								otherTiles={otherTiles}
								highlightItems={pageConfig.highlighted_items}
								quickShipItems={pageConfig.quick_ship}
								dynamicPageState={dynamicPageState}
								pageLoading={pageLoading}
								tileSort={tileSort}
								isEastCoast={isEastCoast}
								hideEncompass={pageData.url === '/shop/windows/stock/' && !isEastCoast}
								seo={pageData.seo}
								inlinePromos={pageConfig.inline_offramps}
								isLogged={isLogged}
							/>

							{/* Empty State */}
							{(!tiles || tiles?.length === 0) &&
								pageConfig.no_results_text?.[0] &&
								pageConfig.no_results_text
									.filter((results) => results.product_type.includes(pageData.department))
									.map((item, index) => (
										<div
											className='mb-8 mt-4 xl:mb-12 xl:mt-6'
											key={index}
										>
											{item.heading && <h2>{item.heading}</h2>}
											{item.description && (
												<div
													className='text-body-2 font-semibold'
													dangerouslySetInnerHTML={{ __html: item.description }}
												/>
											)}

											{item.offramps?.help_option?.[0] && (
												<div className={`mt-3 bg-gray-10 px-5 py-4 md:p-6 lg:max-w-screen-md xl:mt-5`}>
													{pageConfig.mobile_contact_us && <p className='text-center md:hidden'>{pageConfig.mobile_contact_us}</p>}

													<Row>
														{item.offramps.help_option.map((help, index) => {
															const date = new Date();
															const hour = date.getHours();
															const day = date.getDay();

															if ((day === 0 || day === 6 || hour < 8 || hour >= 16) && index > 0) return null;

															return (
																<Column
																	xs={12}
																	md={6}
																	key={index}
																>
																	<div
																		className='h-full py-4 md:px-14 md:py-0 [&>p:first-child]:flex md:[&>p:first-child]:justify-center md:[&_p]:text-center [&_span]:mr-2.5' // update these styles to match figma
																		dangerouslySetInnerHTML={{ __html: help }}
																	/>
																</Column>
															);
														})}
													</Row>
												</div>
											)}
										</div>
									))}
						</div>
					</div>
				</div>
			</Container>

			{isValidArray(pageData?.offramps?.card) && (
				<VisualNav
					placement={'bottom'}
					pageData={pageData}
				/>
			)}

			{isValidArray(pageData?.evergreen_content) && (
				<div>
					{pageData.evergreen_content.map((block, index) => {
						if (block.cards != null && block.cards.cards?.[0]) {
							return (
								<CardSection
									data={block.cards}
									key={index}
								/>
							);
						}

						if (block.faqs != null && block.faqs.group?.[0]) {
							return (
								<div
									key={index}
									className='mb-12 mt-10 md:mt-12'
								>
									<Container>
										{block.faqs.heading && <h2 className='text-center'>{block.faqs.heading}</h2>}

										<div className={`break-inside-avoid [&>div]:mb-[30px] ${block.faqs.group.length == 1 ? 'gap-0 lg:grid-cols-1 lg:[&>div]:mb-0' : 'lg:grid-cols-2 lg:gap-12'}`}>
											{block.faqs.group.map((group, index) => (
												<React.Fragment key={index}>
													{isValidArray(group?.faq) && (
														<div>
															{group.heading && <p>{group.heading}</p>}
															<FAQ
																data={group.faq}
																noStructuredData={true}
																getFAQData={fetchFAQData}
																multipleColumns={block.faqs.group.length == 1}
															/>
														</div>
													)}
												</React.Fragment>
											))}
										</div>

										{block.faqs.ctas?.[0] && (
											<div className='mt-[30px] text-center lg:mt-[50px]'>
												<CTA data={block.faqs.ctas[0]} />
											</div>
										)}
									</Container>
								</div>
							);
						}
					})}
				</div>
			)}
			{!loading && isValidArray(faqData) && <Question faq={faqData} />}
		</Layout>
	);
};

export default PLP;

PLP.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => (
	<SEO
		// eslint-disable-next-line react/prop-types -- fix this
		{...data.contentstackStaticPlp.seo}
		// eslint-disable-next-line react/prop-types -- fix this
		pageLabel={!data.contentstackStaticPlp.manual ? 'plp-dynamic' : 'plp-static'}
	/>
);

export const query = graphql`
	query ($plpId: String!) {
		contentstackStaticPlp(id: { eq: $plpId }) {
			url
			department
			seo {
				...SEO
			}
			breadcrumb {
				active_page
				back_pages {
					...CTA
				}
			}
			offramps {
				heading
				card {
					...ImageWithContent
				}
			}
			dynamic_filters
			filters
			manual
			request_body
			tiles {
				...Tile
			}
			top_content {
				description
				heading
				subheading
				image_filters {
					id
					heading
					image {
						...Image
						image {
							gatsbyImageData(height: 100, quality: 90)
						}
					}
					ctas {
						...CTA
					}
				}
			}
			evergreen_content {
				cards {
					...CardSection
				}
				manual_entries {
					heading
					group {
						image {
							...Image
							image {
								gatsbyImageData(height: 300, quality: 90)
							}
						}
						heading
						subheading
						cta_placement
						ctas {
							...CTA
						}
					}
				}
				faqs {
					heading
					group {
						heading
						faq {
							question
							answer
						}
					}
					ctas {
						...CTA
					}
				}
				dynamic {
					heading
					dynamic {
						...Dynamic
					}
				}
				finder_help {
					heading
					subheading
					cta_ribbon {
						ribbon_style
						hide_on_mobile
						title
						group {
							background_color
							behind_image_color
							subheading
							heading
							disclaimer
							ctas {
								...CTA
							}
						}
					}
				}
			}
		}
		contentstackPlpConfig {
			inline_offramps {
				type
				heading
				description
				cta {
					...CTA
				}
				image {
					url
				}
			}
			errors {
				error
				field_id
			}
			highlighted_items
			quick_ship
			screen_finder {
				heading
				cta {
					...CTA
				}
			}
			mobile_contact_us
			no_results_text {
				product_type
				heading
				description
				offramps {
					help_option
				}
			}
		}
		allProductTiles {
			nodes {
				...ProductTile
			}
		}
	}
`;
